import { Link } from 'react-router-dom';
import { useState } from 'react';
import { categories } from '@/mocks/categories';
import { videos, getFeaturedVideos, getHotVideos, getNewVideos } from '@/mocks/videos';
import { getLatestArticles } from '@/mocks/articles';
import { faqItems } from '@/mocks/faq';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function Home() {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const featuredVideos = getFeaturedVideos();
  const hotVideos = getHotVideos();
  const newVideos = getNewVideos();
  const latestArticles = getLatestArticles(3);

  const featuredHero = featuredVideos[0] || videos[0];

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '极速影视',
        url: 'https://chentaotao.cn',
        description: '极速不缓冲高清在线观看平台，免费看电视剧全集和最新电影',
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '400-888-6666',
          contactType: 'customer service',
          availableLanguage: ['Chinese'],
        },
      },
      {
        '@type': 'WebSite',
        name: '极速影视',
        url: 'https://chentaotao.cn',
        description: '极速不缓冲高清在线观看，流畅播放不卡顿，免费看全集',
        potentialAction: {
          '@type': 'SearchAction',
          'target': 'https://chentaotao.cn/search?q={search_term_string}',
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'WebPage',
        name: '极速影视 - 首页',
        description: '极速影视平台主打极速不缓冲高清在线观看体验',
        url: 'https://chentaotao.cn',
        isPartOf: { '@type': 'WebSite', name: '极速影视' },
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqItems.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      <section className="relative w-full h-[520px] md:h-[650px] flex items-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Dark%20cinematic%20home%20theater%20room%20with%20large%20screen%20displaying%20colorful%20abstract%20movie%20visuals%2C%20warm%20ambient%20lighting%2C%20modern%20minimalist%20interior%2C%20cozy%20atmosphere%2C%20premium%20entertainment%20setup%2C%20soft%20glow&width=1800&height=900&seq=hero-bg-home&orientation=landscape"
          alt="极速影视 - 高清在线观影体验"
          title="极速影视 - 极速不缓冲高清在线观看"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>

        <div className="relative z-10 w-full px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-500/90 text-background-50 text-sm font-medium mb-6">
              <i className="ri-flashlight-fill"></i>
              <span>秒开即播 · 极速不缓冲</span>
            </div>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-black text-background-50 mb-6 leading-tight">
              极速<span className="text-accent-400">不缓冲</span>
              <br className="md:hidden" />
              高清在线观看
            </h1>
            <p className="text-base md:text-lg text-background-50/80 mb-8 max-w-2xl mx-auto leading-relaxed">
              免费看电视剧全集和最新电影，极速高清不卡，秒开即播随心追。
              极速影视平台为您带来前所未有的流畅观影体验。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/dianshiju"
                className="w-full sm:w-auto px-8 py-3.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-full text-base whitespace-nowrap cursor-pointer transition-all hover:scale-105"
              >
                <i className="ri-play-circle-fill mr-2"></i>
                立即观看
              </Link>
              <Link
                to="/about"
                className="w-full sm:w-auto px-8 py-3.5 bg-background-50/20 hover:bg-background-50/30 backdrop-blur-sm text-background-50 font-semibold rounded-full text-base whitespace-nowrap cursor-pointer transition-all border border-background-50/30"
              >
                <i className="ri-information-line mr-2"></i>
                了解更多
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
          <div className="flex items-center gap-2 text-background-50/60 text-xs">
            <span>向下滚动探索</span>
            <i className="ri-arrow-down-line animate-bounce"></i>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              全部分类
            </h2>
            <p className="text-foreground-500 text-sm md:text-base">
              海量影视资源，总有一款适合你
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group relative bg-background-50 border border-background-200 rounded-2xl p-5 md:p-6 text-center cursor-pointer transition-all hover:border-primary-200 hover:-translate-y-1"
              >
                <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-3 flex items-center justify-center rounded-xl bg-primary-100 group-hover:bg-primary-500 transition-colors">
                  <i className={`${cat.icon} text-2xl text-primary-600 group-hover:text-background-50 transition-colors`}></i>
                </div>
                <h3 className="font-heading text-base md:text-lg font-bold text-foreground-900 mb-1">
                  {cat.name}
                </h3>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  {cat.description}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                <i className="ri-fire-fill text-accent-500 mr-2"></i>
                热门推荐
              </h2>
              <p className="text-foreground-500 text-sm">大家都在看的精彩内容</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {hotVideos.slice(0, 8).map((video) => (
              <Link
                key={video.id}
                to={`/category/${video.category}`}
                className="group bg-background-50 rounded-2xl overflow-hidden cursor-pointer transition-all hover:-translate-y-1"
                data-product-shop="true"
              >
                <div className="relative w-full aspect-video overflow-hidden">
                  <img
                    src={video.image}
                    alt={video.title}
                    title={`${video.title} - 极速影视高清在线观看`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/60 text-background-50 text-xs font-medium backdrop-blur-sm">
                    {video.status}
                  </div>
                  <div className="absolute top-3 right-3 px-2 py-1 rounded-full bg-primary-500/90 text-background-50 text-xs font-medium flex items-center gap-1">
                    <i className="ri-star-fill text-[10px]"></i>
                    {video.rating}
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500 text-background-50">
                      <i className="ri-play-fill text-2xl"></i>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-heading text-base font-bold text-foreground-900 mb-1 line-clamp-1">
                    {video.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mb-2 line-clamp-2">
                    {video.description}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-foreground-400">
                    <span>{video.year}</span>
                    <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                    <span>{video.categoryName}</span>
                    {video.episodes > 1 && (
                      <>
                        <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                        <span>{video.episodes}集</span>
                      </>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                <i className="ri-sparkling-line text-primary-500 mr-2"></i>
                最新上线
              </h2>
              <p className="text-foreground-500 text-sm">每日更新，精彩不断</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {newVideos.map((video) => (
              <Link
                key={video.id}
                to={`/category/${video.category}`}
                className="group bg-background-50 border border-background-200 rounded-2xl overflow-hidden cursor-pointer transition-all hover:-translate-y-1"
                data-product-shop="true"
              >
                <div className="relative w-full aspect-video overflow-hidden">
                  <img
                    src={video.image}
                    alt={video.title}
                    title={`${video.title} - 极速影视最新上线`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3 px-2 py-0.5 rounded bg-accent-500 text-background-50 text-[10px] font-bold">
                    NEW
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500 text-background-50">
                      <i className="ri-play-fill text-2xl"></i>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-heading text-base font-bold text-foreground-900 mb-1 line-clamp-1">
                    {video.title}
                  </h3>
                  <div className="flex items-center gap-3 text-xs text-foreground-400">
                    <span>{video.year}</span>
                    <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                    <span>{video.categoryName}</span>
                    <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                    <span className="text-primary-500 font-medium">{video.status}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="relative w-full px-4 md:px-6 py-14 md:py-20 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20cinematic%20gradient%20background%20with%20warm%20golden%20and%20deep%20red%20tones%2C%20smooth%20flowing%20curves%2C%20luxurious%20atmosphere%2C%20movie%20premiere%20vibe%2C%20soft%20bokeh%20effects&width=1800&height=800&seq=featured-bg&orientation=landscape"
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-black/70"></div>
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-3">
              精选推荐
            </h2>
            <p className="text-background-50/70 text-sm md:text-base">
              编辑团队精心挑选，品质保证
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {featuredVideos.slice(0, 4).map((video, index) => (
              <Link
                key={video.id}
                to={`/category/${video.category}`}
                className="group flex gap-4 bg-background-50/10 backdrop-blur-sm border border-background-50/20 rounded-2xl p-4 cursor-pointer transition-all hover:bg-background-50/20"
                data-product-shop="true"
              >
                <div className="relative w-40 md:w-48 aspect-video rounded-xl overflow-hidden flex-shrink-0">
                  <img
                    src={video.image}
                    alt={video.title}
                    title={`${video.title} - 极速影视精选推荐`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-2 left-2 w-6 h-6 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold">
                    {index + 1}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 rounded-full bg-accent-500/20 text-accent-300 text-xs">
                      {video.categoryName}
                    </span>
                    <span className="flex items-center gap-1 text-accent-400 text-xs">
                      <i className="ri-star-fill text-[10px]"></i>
                      {video.rating}
                    </span>
                  </div>
                  <h3 className="font-heading text-lg font-bold text-background-50 mb-2 line-clamp-1">
                    {video.title}
                  </h3>
                  <p className="text-sm text-background-50/60 line-clamp-2 leading-relaxed">
                    {video.description}
                  </p>
                  <div className="flex items-center gap-2 mt-3 text-xs text-background-50/50">
                    <span>{video.year}</span>
                    <span>{video.status}</span>
                    {video.episodes > 1 && <span>{video.episodes}集</span>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                <i className="ri-article-line text-secondary-500 mr-2"></i>
                影视资讯
              </h2>
              <p className="text-foreground-500 text-sm">最新影视动态与深度解析</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {latestArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.id}`}
                className="group bg-background-50 border border-background-200 rounded-2xl overflow-hidden cursor-pointer transition-all hover:-translate-y-1"
              >
                <div className="relative w-full aspect-[16/10] overflow-hidden">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    title={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-background-50/90 text-foreground-700 text-xs font-medium backdrop-blur-sm">
                    {article.categoryName}
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-heading text-base font-bold text-foreground-900 mb-2 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed mb-4">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-foreground-400">
                    <span>{article.publishDate}</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {article.readCount.toLocaleString()}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              常见问题
            </h2>
            <p className="text-foreground-500 text-sm">
              关于极速影视，你想知道的都在这里
            </p>
          </div>
          <div className="flex flex-col gap-3">
            {faqItems.map((item, index) => (
              <div
                key={index}
                className="bg-background-50 border border-background-200 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-background-100 transition-colors"
                >
                  <span className="font-medium text-foreground-800 text-sm md:text-base pr-4">
                    {item.question}
                  </span>
                  <i
                    className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-300 flex-shrink-0 ${
                      activeFaq === index ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>
                {activeFaq === index && (
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {item.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}